<template>
  <Teleport to="body">
    <Transition name="fade">
      <div v-if="open" class="fixed inset-0 z-40 bg-black/20 backdrop-blur-[1px]" @click="$emit('close')"></div>
    </Transition>

    <Transition name="slide-over">
      <div v-if="open" class="fixed inset-y-0 right-0 z-50 w-full sm:w-[85%] bg-white dark:bg-[#1C2128] shadow-2xl flex flex-col">

        <!-- Header -->
        <div class="flex-shrink-0 flex items-center justify-between gap-3 px-5 sm:px-6 py-4 border-b border-gray-100 dark:border-gray-700">
          <div class="min-w-0">
            <h2 class="text-base font-semibold text-gray-900 dark:text-white truncate">
              {{ isEdit ? 'Edit role' : 'New role' }}
            </h2>
            <p class="text-xs text-gray-400 dark:text-gray-500 truncate">
              Listed publicly at /careers once it is live
            </p>
          </div>

          <div class="flex items-center gap-2 flex-shrink-0">
            <a v-if="isEdit && careerJob.publish" :href="careerJob.public_path" target="_blank" rel="noopener"
              class="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors">
              <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="M13.5 6H5.25A2.25 2.25 0 0 0 3 8.25v10.5A2.25 2.25 0 0 0 5.25 21h10.5A2.25 2.25 0 0 0 18 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25"/>
              </svg>
              View
            </a>
            <button type="button" @click="$emit('close')"
              class="px-4 py-1.5 text-sm font-medium text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors">
              Cancel
            </button>
            <button type="button" @click="submit('save')" :disabled="saving || !canSave"
              class="px-4 py-1.5 text-sm font-semibold text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors">
              {{ saving ? 'Saving...' : (isEdit ? 'Save' : 'Save draft') }}
            </button>
            <button v-if="!isLive" type="button" @click="submit('publish')" :disabled="saving || !canSave"
              class="px-4 py-1.5 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors">
              Publish
            </button>
          </div>
        </div>

        <!-- Body -->
        <div class="flex-1 min-h-0 overflow-y-auto">
          <div class="mx-auto max-w-3xl px-5 sm:px-6 py-6 space-y-6">

            <p v-if="error" class="px-3.5 py-2.5 rounded-lg bg-red-50 dark:bg-red-900/20 text-sm font-medium text-red-600 dark:text-red-400">
              {{ error }}
            </p>

            <!-- Title -->
            <div>
              <label class="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">
                Job title<span class="text-red-500">*</span>
              </label>
              <input ref="titleInput" v-model="title" type="text" autocomplete="off"
                placeholder="e.g. Senior Rails Engineer"
                class="w-full px-3.5 py-2.5 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-[#0d1117] text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-shadow" />
            </div>

            <!-- Type + location -->
            <div class="grid gap-5 sm:grid-cols-2">
              <div>
                <label class="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">
                  Work type
                </label>
                <div class="grid grid-cols-3 gap-2">
                  <button v-for="option in CATEGORY_OPTIONS" :key="option.value" type="button"
                    @click="category = option.value"
                    class="px-3 py-2.5 rounded-lg border text-sm font-semibold transition-colors"
                    :class="category === option.value
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 ring-1 ring-blue-500'
                      : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'">
                    {{ option.label }}
                  </button>
                </div>
              </div>
              <div>
                <label class="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">
                  Location<span class="text-red-500">*</span>
                </label>
                <input v-model="location" type="text" autocomplete="off"
                  placeholder="e.g. Lahore, or Remote (Europe)"
                  class="w-full px-3.5 py-2.5 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-[#0d1117] text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-shadow" />
              </div>
            </div>

            <!-- Salary + deadline -->
            <div class="grid gap-5 sm:grid-cols-2">
              <div>
                <label class="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">
                  Salary <span class="text-gray-400 dark:text-gray-500 font-normal normal-case">(optional)</span>
                </label>
                <input v-model="salary" type="text" placeholder="e.g. £70k to £90k"
                  class="w-full px-3.5 py-2.5 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-[#0d1117] text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-shadow" />
              </div>
              <div>
                <label class="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">
                  Application deadline
                </label>
                <input v-model="deadline" type="date"
                  class="w-full px-3.5 py-2.5 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-[#0d1117] text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-shadow" />
                <p class="mt-1.5 text-xs" :class="deadlineHintClass">{{ deadlineHint }}</p>
              </div>
            </div>

            <!-- Summary -->
            <div>
              <div class="flex items-baseline justify-between mb-1.5">
                <label class="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Short summary<span class="text-red-500">*</span>
                </label>
                <span class="text-xs tabular-nums" :class="seoDescription.length > SUMMARY_LIMIT ? 'text-red-500 font-semibold' : 'text-gray-400 dark:text-gray-500'">
                  {{ seoDescription.length }}/{{ SUMMARY_LIMIT }}
                </span>
              </div>
              <textarea v-model="seoDescription" rows="3"
                placeholder="One or two sentences. This is the card on /careers and the description in search results."
                class="w-full px-3.5 py-2.5 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-[#0d1117] text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-shadow resize-y"></textarea>
            </div>

            <!-- Description -->
            <div>
              <label class="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">
                Full description<span class="text-red-500">*</span>
              </label>
              <quill-editor
                :content="description"
                :enable="true"
                :enable-table="true"
                :opts="{ height: '360px' }"
                @update-content="description = $event" />
              <p class="mt-1.5 text-xs" :class="plainDescriptionLength > 0 && plainDescriptionLength < 20 ? 'text-red-500 font-medium' : 'text-gray-400 dark:text-gray-500'">
                What the role involves and who it suits. At least 20 characters.
              </p>
            </div>

            <!-- Interview link -->
            <div>
              <label class="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">
                Interview booking link <span class="text-gray-400 dark:text-gray-500 font-normal normal-case">(optional)</span>
              </label>
              <input v-model="eventLink" type="text" placeholder="A booking link sent with the confirmation email"
                class="w-full px-3.5 py-2.5 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-[#0d1117] text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-shadow" />
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { ref, computed, watch, nextTick } from 'vue'
import { CATEGORY_OPTIONS } from 'stores/admin_career_jobs.js'

const SUMMARY_LIMIT = 300

const props = defineProps({
  // Kept mounted between opens so the backdrop fade and the panel slide can
  // animate both in and out — same as ReleaseNoteFormSlideOver.
  open: { type: Boolean, default: false },
  saving: { type: Boolean, default: false },
  error: { type: String, default: '' },
  // Null for a new role; the detail-view record when editing.
  careerJob: { type: Object, default: null },
})

const emit = defineEmits(['close', 'submit'])

const title = ref('')
const location = ref('')
const salary = ref('')
const deadline = ref('')
const category = ref('remote')
const seoDescription = ref('')
const description = ref('')
const eventLink = ref('')
const titleInput = ref(null)

const isEdit = computed(() => !!props.careerJob?.id)
const isLive = computed(() => props.careerJob?.status === 'published')

// Quill leaves "<p><br></p>" behind in an editor that has been cleared, which
// is not blank to the server but is blank to a reader. The model requires 20
// characters of actual text, so the check has to match that.
const plainDescriptionLength = computed(() =>
  description.value.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim().length)

const canSave = computed(() =>
  title.value.trim().length > 0
  && location.value.trim().length > 0
  && seoDescription.value.trim().length > 0
  && seoDescription.value.length <= SUMMARY_LIMIT
  && plainDescriptionLength.value >= 20)

const isPastDeadline = computed(() => {
  if (!deadline.value) return false
  return new Date(`${deadline.value}T23:59:59`) < new Date()
})

const deadlineHint = computed(() => {
  if (!deadline.value) return 'Leave empty to keep it open indefinitely.'
  if (isPastDeadline.value) return 'In the past, so this role will not appear on /careers.'
  return 'After this date it drops off /careers but keeps its own page.'
})

const deadlineHintClass = computed(() =>
  (isPastDeadline.value ? 'text-amber-600 dark:text-amber-400' : 'text-gray-400 dark:text-gray-500'))

watch(() => props.open, (isOpen) => {
  if (!isOpen) return

  const record = props.careerJob
  title.value = record?.title ?? ''
  location.value = record?.location ?? ''
  salary.value = record?.salary ?? ''
  deadline.value = record?.deadline ?? ''
  category.value = record?.category ?? 'remote'
  seoDescription.value = record?.seo_description ?? ''
  description.value = record?.description ?? ''
  eventLink.value = record?.event_link ?? ''

  if (!record) nextTick(() => titleInput.value?.focus())
})

function submit(intent) {
  if (!canSave.value) return

  emit('submit', {
    intent,
    attributes: {
      title: title.value.trim(),
      location: location.value.trim(),
      salary: salary.value.trim(),
      deadline: deadline.value || null,
      category: category.value,
      seo_description: seoDescription.value.trim(),
      description: description.value,
      event_link: eventLink.value.trim(),
    },
  })
}
</script>

<style scoped>
.slide-over-enter-active { transition: transform 0.25s ease; }
.slide-over-leave-active { transition: transform 0.20s ease; }
.slide-over-enter-from,
.slide-over-leave-to    { transform: translateX(100%); }

.fade-enter-active { transition: opacity 0.2s ease; }
.fade-leave-active { transition: opacity 0.15s ease; }
.fade-enter-from,
.fade-leave-to    { opacity: 0; }
</style>
